import { RouterProvider, createBrowserRouter, createMemoryRouter } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import EveThemeProvider from '@ashn/eve-themes-react/EveThemeProvider';
import { ThemeProvider } from '@mui/material/styles';
import useReactRoutes from './components/shared/useReactRoutes';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import GlobalStyles from '@mui/material/GlobalStyles';
import { initialize } from '@ashn/component-library-react/Amplitude';
import useFeatureFlags from './data/queryHooks/useFeatureFlags';
import { excludedRoutesForAutocapture } from '@ashn/identity-react/Identity';
import { buildAutocaptureAllowlistRegex, pageViewTitlePlugin } from './utils/analytics';

const routerCreate =
	process.env.NODE_ENV === 'test' ? createMemoryRouter : createBrowserRouter;

const elementInteractionsRegex = buildAutocaptureAllowlistRegex(excludedRoutesForAutocapture);

const App = () => {
	const routes = useReactRoutes();
	const amplitudeInitializedRef = useRef(false);

	const { analyticsEnabled } = useFeatureFlags();
	const router = useMemo(() => routerCreate(routes), [routes]);

	useEffect(() => {
		if (!analyticsEnabled || amplitudeInitializedRef.current) {
			return;
		}

		amplitudeInitializedRef.current = true;

		const options = {
			autocapture: {
				elementInteractions: {
					pageUrlAllowlist: [
						elementInteractionsRegex
					],
				},
				frustrationInteractions: true,
				formInteractions: false,
				webVitals: true,
				networkTracking: true,
				fileDownloads: false,
			},
		};

		(async function () {
			try {
				const amplitude = await import('@amplitude/analytics-browser');
				initialize({ amplitude, options });
				amplitude.add(pageViewTitlePlugin);
			} catch (error) {
				amplitudeInitializedRef.current = false;
			}
		})();
	}, [analyticsEnabled]);

	return (
		<EveThemeProvider themeKey="ashcare" themeMode="light">
			<GlobalStyles styles={{ 'html, body': { overflowX: 'hidden', maxWidth: '100%' } }} />
			<ThemeProvider theme={(outerTheme) => ({ ...outerTheme, zIndex: { ...outerTheme.zIndex, modal: 10002 } })}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<RouterProvider router={router} />
				</LocalizationProvider>
			</ThemeProvider>
		</EveThemeProvider>
	);
};

export default App;
